//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDebounce } from 'use-debounce';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import {
  BodySecondaryText,
  BodyText,
  MetatagText,
  SubsectionText,
} from '@components/atoms/text/Text';
import {
  DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  TablePaginationControls,
} from '@components/molecules/pagination/TablePaginationControls';
import Cuid from '@models/Cuid';
import { ProductType } from '@models/IItemMetadata';
import { UiaProductItem } from '@uia/products/UiaProductItem';
import {
  CRAWL_PRODUCT_TYPES,
  MAX_NUM_CATALOG_SUGGESTIONS,
  SEARCH_DEBOUNCE_DELAY,
  UiaProductTrackingIds,
  uiaProductPrefix,
} from '@uia/products/constants/UiaProductConstants';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { useGetCrawledProductsQuery } from '@uia/products/hooks/useGetCrawledProductsQuery';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';
import { isValidUrlWithProtocol } from '@utils/urlValidation';

export interface UiaProductGridProps {
  enableEditor: (product: CuralateCatalogProduct) => void;
  selectedProducts: CuralateCatalogProduct[];
  setSelectedProducts: (selectedProducts: CuralateCatalogProduct[]) => void;
  tagSingleProduct: (product: CuralateCatalogProduct) => void;
}

export const UiaProductGrid = ({
  enableEditor,
  selectedProducts,
  setSelectedProducts,
  tagSingleProduct,
}: UiaProductGridProps) => {
  const theme = useTheme();
  const {
    cuid,
    handlePageChange,
    isCatalogSuggestions,
    isLoadingProducts,
    limit,
    metadata,
    offset,
    products: productsResults,
    searchQuery,
    selectedProductToEdit,
    totalCount,
  } = useProductTaggingContext();

  const isEditorEnabled = isDefined(selectedProductToEdit);

  const products = useMemo(() => {
    return isCatalogSuggestions
      ? (productsResults ?? []).slice(0, MAX_NUM_CATALOG_SUGGESTIONS)
      : productsResults ?? [];
  }, [isCatalogSuggestions, productsResults]);

  const [debouncedSearchQuery] = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY,
  );

  const { data: crawledProducts, isFetching: isCrawlingProducts } =
    useGetCrawledProductsQuery({
      url: debouncedSearchQuery,
      universalItemId: Cuid.toPartial(cuid),
    });

  const createUrlTag = () => {
    if (isDefined(crawledProducts?.[0])) {
      enableEditor(crawledProducts[0]);
    }
  };

  const selectProduct = (product: CuralateCatalogProduct) => {
    if (
      !selectedProducts.find(
        (item) => item.productStyleId === product.productStyleId,
      )
    ) {
      setSelectedProducts([...selectedProducts, product]);
    } else {
      setSelectedProducts(
        selectedProducts.filter(
          (item) => item.productStyleId !== product.productStyleId,
        ),
      );
    }
  };

  useEffect(() => {
    // If we only have a craweledProduct then send the user directly to the editor mode
    // so they can edit the crawled product
    if (
      isDefined(crawledProducts?.[0]) &&
      products.every((product) =>
        CRAWL_PRODUCT_TYPES.includes(product.productType as ProductType),
      )
    ) {
      enableEditor(crawledProducts[0]);
    }
  }, [crawledProducts, products, enableEditor]);

  if (isEditorEnabled) {
    return null;
  }

  return searchQuery && isLoadingProducts ? (
    <CenteredSpinner />
  ) : (
    <>
      {products.length > 0 && (
        <div>
          {isCatalogSuggestions && (
            <CatalogSuggestionsWrapper>
              <CatalogSuggestionsText>
                <MetatagText>Detected Products</MetatagText>
                <BodySecondaryText>
                  {Math.min(MAX_NUM_CATALOG_SUGGESTIONS, products.length)} Items
                </BodySecondaryText>
              </CatalogSuggestionsText>
            </CatalogSuggestionsWrapper>
          )}
          {!isCatalogSuggestions && (
            <BodyText>{`${pluralize(totalCount, 'product')} found`}</BodyText>
          )}

          <ProductGrid>
            {products.map((product) => (
              <UiaProductItem
                key={product.destinationUrl}
                isAlreadyTagged={metadata.productTags.some(
                  (item) => item.productStyleId === product.productStyleId,
                )}
                isSelected={selectedProducts.some(
                  (item) => item.productStyleId === product.productStyleId,
                )}
                product={product}
                selectProduct={() => selectProduct(product)}
                tagSingleProduct={tagSingleProduct}
              />
            ))}
          </ProductGrid>
          {!isCatalogSuggestions && (
            <TablePaginationControlsWrapper>
              <TablePaginationControls
                currentPage={offset / limit}
                dropdownContentZIndex={theme.zindex.popover} // To ensure pagination dropdown select appears on top of the UIA modal
                handlePageChange={handlePageChange}
                itemsPerPage={limit}
                itemsPerPageOptions={DEFAULT_ITEMS_PER_PAGE_OPTIONS}
                numItems={totalCount}
                trackingIdPrefix={uiaProductPrefix}
              />
            </TablePaginationControlsWrapper>
          )}
        </div>
      )}
      {!!products.length && isValidUrlWithProtocol(searchQuery) && (
        <div>
          <StyledBodyText>
            <Icon name='info' /> Not seeing the product you were expecting?
          </StyledBodyText>
          <BodyText>
            You may choose to tag with a URL instead.{' '}
            <PrimaryLinkButton
              onClick={createUrlTag}
              trackingId={UiaProductTrackingIds.CREATE_URL_TAG_BUTTON}
            >
              Create URL tag
            </PrimaryLinkButton>
          </BodyText>
          {isCrawlingProducts && (
            <RetrievingProductInfoContainer>
              <CenteredSpinner />
              <BodyText>
                Retrieving product information for this URL...
              </BodyText>
            </RetrievingProductInfoContainer>
          )}
        </div>
      )}
      {products.length === 0 &&
        !crawledProducts?.length &&
        !isEmptyValue(searchQuery) &&
        !isLoadingProducts &&
        !isCrawlingProducts && (
          <NoResultsContainer>
            <SubsectionText>No Products Found</SubsectionText>
            <BodyText>Change your search term and try again.</BodyText>
          </NoResultsContainer>
        )}
    </>
  );
};

const ProductGrid = styled.div(
  ({ theme }) => `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]} 0;
  `,
);

const CatalogSuggestionsText = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
  `,
);

const CatalogSuggestionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NoResultsContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: ${theme.spacings[3]};
  `,
);

const TablePaginationControlsWrapper = styled.div(
  ({ theme }) => `
    margin-top: ${theme.spacings[3]};
  `,
);

const StyledBodyText = styled(BodyText)(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings[2]};
  `,
);

const RetrievingProductInfoContainer = styled(BodyText)(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]};
    width: 100%;
  `,
);
